import { formatSydneyDateShort } from './helpers';

/** Status tones shared by pills, bars and timeline dots. */
export type PerformanceTone =
  | 'muted'
  | 'slate'
  | 'success'
  | 'warning'
  | 'danger'
  | 'link'
  | 'solid';

export const performancePillClass = (tone: PerformanceTone | string = 'muted') => {
  switch (tone) {
    case 'success':
      return 'border-success-border bg-success-bg text-success';
    case 'warning':
      return 'border-warning-border bg-warning-bg text-warning';
    case 'danger':
      return 'border-destructive-border bg-destructive-bg text-destructive';
    case 'link':
      return 'border-link/30 bg-selection text-link';
    case 'solid':
      return 'border-primary bg-primary text-primary-foreground';
    case 'slate':
      return 'border-border bg-muted text-foreground';
    default:
      return 'border-border bg-card text-muted-foreground';
  }
};

export const reviewStatusTone = (status: string): PerformanceTone => {
  switch (status) {
    case 'Manager Submitted':
    case 'Released':
      return 'success';
    case 'Awaiting Manager':
      return 'link';
    case 'Manager In Progress':
    case 'Self-review In Progress':
      return 'warning';
    default:
      return 'muted';
  }
};

export const scheduleStatusTone = (status: string): PerformanceTone => {
  switch (status) {
    case 'Active':
      return 'success';
    case 'Calibration':
      return 'warning';
    case 'Closed':
      return 'slate';
    default:
      return 'muted';
  }
};

export const formatPerformanceDate = (value?: string | null) => {
  if (!value) return '–';
  const date = String(value).slice(0, 10);
  const [year, month, day] = date.split('-').map(Number);
  if (!year || !month || !day) return formatSydneyDateShort(value) || '–';
  return `${day} ${['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][month - 1]} ${year}`;
};

export const formatPerformanceDateTime = (value?: string | null) => {
  if (!value) return '–';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '–';
  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Australia/Sydney',
  }).format(date);
};

export const formatScore = (value: number | null | undefined) =>
  value == null ? '–' : Number(value).toFixed(2);

export const formatGap = (value: number | null | undefined) => {
  if (value == null) return '–';
  if (Math.abs(value) < 0.005) return '0.00';
  return `${value > 0 ? '+' : '−'}${Math.abs(value).toFixed(2)}`;
};

export const gapTone = (value: number | null | undefined): PerformanceTone => {
  if (value == null || Math.abs(value) < 0.005) return 'muted';
  return value > 0 ? 'warning' : 'link';
};

export const scoreBand = (
  score: number | null | undefined,
  levels: { score: number; label: string }[]
) => {
  if (score == null || !levels.length) return null;
  const sorted = [...levels].sort((a, b) => b.score - a.score);
  return sorted.find((level) => score >= level.score - 0.5) || sorted[sorted.length - 1];
};

export const initialsOf = (name?: string) =>
  String(name || '')
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

export const monthSpan = (start?: string | null, end?: string | null) => {
  if (!start || !end) return 0;
  const a = new Date(`${start}T00:00:00Z`).getTime();
  const b = new Date(`${end}T00:00:00Z`).getTime();
  return Math.max(0, Math.round((b - a) / (86400000 * 30.44)));
};

export const weightSegments = (
  goals: { id: number; title: string; weight: number; categoryColour: string; status: string }[]
) => {
  const active = goals.filter((goal) => goal.status !== 'Withdrawn');
  const total = active.reduce((sum, goal) => sum + (Number(goal.weight) || 0), 0);
  const scale = Math.max(total, 100);
  return active.map((goal) => ({
    id: goal.id,
    title: `${goal.title} · ${goal.weight}%`,
    width: `${((Number(goal.weight) || 0) / scale) * 100}%`,
    colour: goal.categoryColour,
  }));
};

export const reviewStatusLabel = (status: string) => status || 'Not Started';

export const reviewWindowLabel = (window: {
  enabled: boolean;
  start: string | null;
  end: string | null;
}) => {
  if (!window.enabled) return 'Off';
  if (!window.start || !window.end) return 'Dates not set';
  return `${formatPerformanceDate(window.start)} – ${formatPerformanceDate(window.end)}`;
};

export const modeLabel = (mode: string) => {
  switch (mode) {
    case 'KeyResults':
      return 'Key results';
    case 'Target':
      return 'Target number';
    default:
      return 'Percentage';
  }
};
